import { fetchAuthSession } from 'aws-amplify/auth';
import axios, { type AxiosRequestConfig } from 'axios';

import { getTenantBackendUrl } from '@/utility/tenant-config';

const baseUrl = getTenantBackendUrl();

async function getAuthHeader(): Promise<Record<'Authorization' | 'x-lang', string>> {
  const { accessToken } = (await fetchAuthSession()).tokens ?? {};
  if (!accessToken) {
    throw new Error('Missing access token');
  }
  return { Authorization: `Bearer ${accessToken}`, 'x-lang': 'de' };
}

export const client = {
  async publicGet<T>(path: string) {
    const config: AxiosRequestConfig = {
      method: 'GET',
      baseURL: baseUrl,
    };
    const response = await axios<T>(path, config);

    return response.data;
  },

  async get<T>(path: string) {
    const config: AxiosRequestConfig = {
      method: 'GET',
      headers: await getAuthHeader(),
      baseURL: baseUrl,
    };
    const response = await axios<T>(`${path}`, config);

    return response.data;
  },

  async post<T>(path: string, body: unknown) {
    const config: AxiosRequestConfig = {
      method: 'POST',
      data: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeader()),
      },
    };

    const response = await axios<T>(`${baseUrl}${path}`, config);

    return response.data;
  },

  async put<T>(path: string, body: unknown) {
    const config: AxiosRequestConfig = {
      method: 'PUT',
      data: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeader()),
      },
    };

    const response = await axios<T>(`${baseUrl}${path}`, config);

    return response.data;
  },

  async patch<T = boolean>(path: string, body: unknown) {
    const config: AxiosRequestConfig = {
      method: 'PATCH',
      data: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeader()),
      },
    };

    const response = await axios<T>(`${baseUrl}${path}`, config);

    return response.data;
  },

  async delete<T = boolean>(path: string, body?: unknown) {
    const config: AxiosRequestConfig = {
      method: 'DELETE',
      headers: {
        ...await getAuthHeader(),
      },
    };

    if (body) {
      config.headers!['Content-Type'] = 'application/json';
      config.data = JSON.stringify(body);
    }

    const response = await axios<T>(`${baseUrl}${path}`, config);

    return response.data;
  },
};
